import React, { useEffect } from "react";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../config/URL";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

function CurriculumEdit({
  id,
  onSuccess,
  curriculumOutletId,
  handleMenuClose,
}) {
  const [show, setShow] = useState(false);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [isModified, setIsModified] = useState(false);

  const handleShow = () => {
    setShow(true);
    setIsModified(false);
  };
  const handleClose = () => {
    handleMenuClose();
    setShow(false);
  };
  const userName = localStorage.getItem("userName");

  const validationSchema = Yup.object({
    curriculumCode: Yup.string().required("*Curriculum Code is required"),
    lessonNo: Yup.string().required("*Lesson No is required"),
    curriculumNo: Yup.string().required("*Curriculum Number is required"),
    status: Yup.string().required("*Status is required"),
    present: Yup.string().nullable().max(1500, "*Max 1500 characters"),
    absent: Yup.string().nullable().max(1500, "*Max 1500 characters"),
  });

  const formik = useFormik({
    initialValues: {
      curriculumCode: "",
      lessonNo: "",
      curriculumNo: "",
      status: "",
      present: "",
      absent: "",
      updatedBy: userName,
      curriculumOutletId: curriculumOutletId,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      const payload = {
        curriculumOutletId: curriculumOutletId,
        curriculumCode: values.curriculumCode,
        lessonNo: values.lessonNo,
        curriculumNo: values.curriculumNo,
        status: values.status,
        updatedBy: userName,
        templateSettings: {
          present: values.present || "",
          absent: values.absent || "",
        },
      };
      try {
        const response = await api.put(
          `/updateCourseCurriculumCode/${id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (response.status === 200) {
          toast.success(response.data.message);
          onSuccess();
          handleClose();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong");
      } finally {
        setLoadIndicator(false);
      }
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: (values) => {
      if (
        Object.values(values).some(
          (value) => value && typeof value === "string" && value.trim() !== "",
        )
      ) {
        setIsModified(true);
      } else {
        setIsModified(false);
      }
    },
  });

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await api.get(
          `/getAllCourseCurriculumCodesById/${id}`,
        );
        const data = response.data;
        formik.setValues({
          curriculumCode: data.curriculumCode || "",
          lessonNo: data.lessonNo || "",
          curriculumNo: data.curriculumNo || "",
          status: data.status || "",
          present: data.templateSettings?.present || "",
          absent: data.templateSettings?.absent || "",
          updatedBy: userName,
          curriculumOutletId: data.curriculumOutletId || curriculumOutletId,
        });
      } catch (error) {
        toast.error("Error fetching data: " + error.message);
      }
    };
    if (show) {
      getData();
    }
  }, [show]);

  return (
    <>
      <p
        className="text-start mb-0 menuitem-style"
        onClick={handleShow}
        style={{
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        Edit
      </p>
      <Dialog
        open={show}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        disableBackdropClick={isModified}
        disableEscapeKeyDown={isModified}
      >
        <DialogTitle className="headColor px-4 py-2 ">
          Edit Curriculum
        </DialogTitle>
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              e.target.tagName !== "TEXTAREA" &&
              !formik.isSubmitting
            ) {
              e.preventDefault();
            }
          }}
          className="p-2"
        >
          <DialogContent className="p-1">
            <div className="container">
              <div className="row">
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Lesson No<span className="text-danger">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("lessonNo")}
                    className={`form-select ${
                      formik.touched.lessonNo && formik.errors.lessonNo
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option value=""></option>
                    {Array.from({ length: 150 }, (_, index) => (
                      <option key={index + 1} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </select>
                  {formik.touched.lessonNo && formik.errors.lessonNo && (
                    <div className="invalid-feedback">
                      {formik.errors.lessonNo}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Curriculum Code<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    onKeyDown={(e) => e.stopPropagation()}
                    {...formik.getFieldProps("curriculumCode")}
                    className={`form-control ${
                      formik.touched.curriculumCode &&
                      formik.errors.curriculumCode
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.curriculumCode &&
                    formik.errors.curriculumCode && (
                      <div className="invalid-feedback">
                        {formik.errors.curriculumCode}
                      </div>
                    )}
                </div>
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Curriculum Number<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    onKeyDown={(e) => e.stopPropagation()}
                    {...formik.getFieldProps("curriculumNo")}
                    className={`form-control ${
                      formik.touched.curriculumNo && formik.errors.curriculumNo
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.curriculumNo &&
                    formik.errors.curriculumNo && (
                      <div className="invalid-feedback">
                        {formik.errors.curriculumNo}
                      </div>
                    )}
                </div>
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Status<span className="text-danger">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("status")}
                    className={`form-select ${
                      formik.touched.status && formik.errors.status
                        ? "is-invalid"
                        : ""
                    }`}
                    aria-label="Default select example"
                  >
                    <option value=""></option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  {formik.touched.status && formik.errors.status && (
                    <div className="invalid-feedback">
                      {formik.errors.status}
                    </div>
                  )}
                </div>
                <div className="col-md-12 col-12 mb-2">
                  <label className="form-label">Present Message</label>
                  {/* <ReactQuill
                    value={formik.values.present}
                    onChange={handlePresentChange}
                    modules={modules}
                    formats={formats}
                    className={`${
                      formik.touched.present && formik.errors.present
                        ? "is-invalid"
                        : ""
                    }`}
                  /> */}
                  <textarea
                    name="present"
                    {...formik.getFieldProps("present")}
                    onKeyDown={(e) => e.stopPropagation()}
                    className={`form-control ${
                      formik.touched.present && formik.errors.present
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.present && formik.errors.present && (
                    <div className="invalid-feedback">
                      {formik.errors.present}
                    </div>
                  )}
                </div>
                <div className="col-md-12 col-12 mb-2">
                  <label className="form-label">Absent Message</label>
                  {/* <ReactQuill
                    value={formik.values.absent}
                    onChange={handleAbsentChange}
                    modules={modules}
                    formats={formats}
                    className={`${
                      formik.touched.absent && formik.errors.absent
                        ? "is-invalid"
                        : ""
                    }`}
                  /> */}
                  <textarea
                    name="absent"
                    {...formik.getFieldProps("absent")}
                    onKeyDown={(e) => e.stopPropagation()}
                    className={`form-control ${
                      formik.touched.absent && formik.errors.absent
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.absent && formik.errors.absent && (
                    <div className="invalid-feedback">
                      {formik.errors.absent}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <button
              type="button"
              className="btn btn-sm btn-border bg-light text-dark"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-button btn-sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              Update
            </button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default CurriculumEdit;
